import React, {
  useState, useEffect, useCallback,
} from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import isEmpty from 'lodash/isEmpty';
import { useDispatch, useSelector } from 'react-redux';
import axios from 'axios';
import { setEventConfigs } from 'models/virtual_event';
import { localizationSlice } from 'models/localization';
import {
  EVENT_CONFIGS_API_URL,
  APP_DOMAIN,
  FETCH_CACHED_EVENT_CONFIGS_API_URL,
  ZENDESK_ID,
  DEFAULT_THEME_COLOR,
} from 'appenv';

import TagManager from 'react-gtm-module';
import { Helmet } from 'react-helmet';
import remoteTimeNow, { remoteTimeInit, addRemoteTimeReadyListener } from 'libs/remote-time-now';
import EventInfoLoadingIndicator from './EventInfoLoadingIndicator';

const Zendesk = ({ id, themeColor }) => {
  const [isLoaded, setLoaded] = useState(false);
  const scriptContent = `
  window.zESettings = {
    webWidget: {
      color: {
        theme: '${themeColor}'
      }
    }
  };
  zE(function(){
    $zopim(function(){
      $zopim.livechat.hideAll();
      $zopim.livechat.window.onHide(function(){$zopim.livechat.hideAll()});
    });
  });
  window.ATL_JQ_PAGE_PROPS = {
    "triggerFunction": function(showCollectorDialog) {
      jQuery("#feedback-button").click(function(e) {
        e.preventDefault();
        showCollectorDialog();
      });
    }
  };
  `;

  const handleScriptInject = ({ scriptTags }) => {
    if (scriptTags) {
      const scriptTag = scriptTags[0];
      scriptTag.onload = () => setLoaded(true);
    }
  };

  return (
    <>
      <Helmet
        script={[{
          id: 'ze-snippet',
          src: `https://static.zdassets.com/ekr/snippet.js?key=${id || 'e9157ef8-de70-470c-a1a2-4c462023dff1'}`,
        }]}
        onChangeClientState={(newState, addedTags) => handleScriptInject(addedTags)}
      />
      {isLoaded && (
        <Helmet>
          <script>
            {scriptContent}
          </script>
        </Helmet>
      )}
    </>
  );
};
Zendesk.propTypes = {
  id: PropTypes.string.isRequired,
  themeColor: PropTypes.string.isRequired,
};

const EventDatetimeContext = React.createContext({
  startDatetime: undefined,
  endDatetime: undefined,
  registerStartDatetime: undefined,
  registerEndDatetime: undefined,
  stageProgrammeRegisterEndDatetime: undefined,
  hasEventStarted: undefined,
  hasEventEnded: undefined,
  isEventLive: undefined,
  hasRegisterStarted: undefined,
  hasRegisterEnded: undefined,
  hasWebinarGoneLive: undefined,
  isOnDemandWebinarReady: undefined,
  hasStageProgrammeRegisterEnded: undefined,
});

const EventDatetimeContextStateProvider = ({ children }) => {
  const dispatch = useDispatch();
  const eventConfigs = useSelector((state) => state.eventConfigs);
  const eventInfoloading = isEmpty(eventConfigs);
  const {
    endTime: endDatetime,
    onDemandWebinarStartTime,
    registrationStartTime: registerStartDatetime,
    registrationEndTime: registerEndDatetime,
    stageProgrammeRegisterEndDatetime,
    startTime: startDatetime,
    webinarStartTime,
  } = eventConfigs;
  const [hasEventStarted, setHasEventStarted] = useState(false);
  const [hasEventEnded, setHasEventEnded] = useState(false);
  const [isEventLive, setIsEventLive] = useState(false);
  const [hasRegisterStarted, setHasRegisterStarted] = useState(false);
  const [hasRegisterEnded, setHasRegisterEnded] = useState(false);
  const [hasWebinarGoneLive, setHasWebinarGoneLive] = useState(false);
  const [isOnDemandWebinarReady, setIsOnDemandWebinarReady] = useState(false);
  const [hasStageProgrammeRegisterEnded, setHasStageProgrammeRegisterEnded] = useState(false);
  const [timeStateInitialized, setTimeStateInitialized] = useState(false);
  const [zendeskId, setZendeskId] = useState(null);
  const [themeColor, setThemeColor] = useState(null);

  const updateStates = useCallback(() => {
    // TODO(ISAAC): MOVE BELOW LOGICS TO REDUX
    if (!eventInfoloading) {
      const now = remoteTimeNow();
      setHasEventStarted(now.isAfter(moment(startDatetime)));
      setHasEventEnded(now.isSameOrAfter(moment(endDatetime)));
      setIsEventLive(now.isAfter(moment(startDatetime)) && !now.isSameOrAfter(moment(endDatetime)));
      setHasRegisterStarted(now.isAfter(moment(registerStartDatetime)));
      setHasRegisterEnded(now.isSameOrAfter(moment(registerEndDatetime)));
      setHasWebinarGoneLive(now.isSameOrAfter(moment(webinarStartTime)));
      setIsOnDemandWebinarReady(now.isSameOrAfter(moment(onDemandWebinarStartTime)));
      setHasStageProgrammeRegisterEnded(now.isAfter(stageProgrammeRegisterEndDatetime));
      setTimeStateInitialized(true);
    }
  }, [endDatetime, eventInfoloading, onDemandWebinarStartTime, registerEndDatetime, registerStartDatetime, stageProgrammeRegisterEndDatetime, startDatetime, webinarStartTime]);

  async function getEventInfo() {
    const appDomain = APP_DOMAIN;
    try {
      let eventConfig;
      try {
        const { data } = await axios.get(EVENT_CONFIGS_API_URL, { params: { event_domain: appDomain } });
        eventConfig = data;
      } catch (e) {
        const injectedCachedEventConfigUrl = document.querySelector('html').getAttribute('data-cached-event-url');
        const staticDataUrl = injectedCachedEventConfigUrl ?? FETCH_CACHED_EVENT_CONFIGS_API_URL;
        console.log('Falling back to cached event configuration API data.');
        const { data } = await axios.get(staticDataUrl);
        eventConfig = data.find((eventInfo) => eventInfo.domain === appDomain);
      }
      dispatch(setEventConfigs(eventConfig));
      dispatch(localizationSlice.actions.setEventTimeZone(eventConfig.timeZone || eventConfig.landing.timeZone));
      setZendeskId(eventConfig?.generalSetting?.zendeskId || ZENDESK_ID);
      setThemeColor(eventConfig.themeColor || DEFAULT_THEME_COLOR);
      const googleTagId = eventConfig?.generalSetting?.googleTagId || eventConfig.googleTagId;
      if (googleTagId) {
        TagManager.initialize({ gtmId: googleTagId });
      }
    } catch (e) {
      console.log(e);
    }
  }

  async function initialize() {
    await Promise.all([
      getEventInfo(),
      remoteTimeInit(),
    ]);
    updateStates();
    addRemoteTimeReadyListener(() => {
      updateStates();
    });
  }

  useEffect(() => {
    initialize();
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      updateStates();
    }, 1000);

    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [registerStartDatetime, updateStates]);

  if (eventInfoloading || !timeStateInitialized) return <EventInfoLoadingIndicator />;

  return (
    <EventDatetimeContext.Provider
      value={{
        startDatetime: moment(startDatetime),
        endDatetime: moment(endDatetime),
        registerStartDatetime: moment(registerStartDatetime),
        registerEndDatetime: moment(registerEndDatetime),
        webinarStartTime: moment(webinarStartTime),
        stageProgrammeRegisterEndDatetime,
        hasEventStarted,
        hasEventEnded,
        isEventLive,
        hasRegisterStarted,
        hasRegisterEnded,
        hasWebinarGoneLive,
        hasStageProgrammeRegisterEnded,
        isOnDemandWebinarReady,
      }}
    >
      {(themeColor && zendeskId) && <Zendesk id={zendeskId} themeColor={themeColor} />}
      {children}
    </EventDatetimeContext.Provider>
  );
};

EventDatetimeContextStateProvider.propTypes = {
  children: PropTypes.node,
};

EventDatetimeContextStateProvider.defaultProps = {
  children: null,
};

export {
  EventDatetimeContext,
  EventDatetimeContextStateProvider,
};
